import clsx from 'clsx'
import map from 'lodash/map'
import React from 'react'

import { ampli } from '@Analytics/ampli'
import { Drawer } from '@Components/Drawer'
import { DrawerContentBasic } from '@Components/Drawer/Content'
import { ImageV2 } from '@Components/Image'
import { Link } from '@Components/Link'
import { ProductCrossSellAndPersonalizationContentFragment } from '@Generated/graphql'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'

import styles from './LinkedProducts.module.scss'

const Item = ({
  className,
  product,
  type,
  ...props
}: React.ComponentProps<typeof Link> & {
  product: ProductCrossSellAndPersonalizationContentFragment
  type: string
}) => {
  const { storeURL } = React.useContext(MetadataContext)

  return (
    <Link
      {...props}
      analytics={{
        dest_page_type: 'product',
        dest_page_type_id: product.id,
        source_id: `linked_products_${type}`,
      }}
      className={clsx(styles.item, className)}
      href={storeURL(product.url || '')}
    >
      {product.primaryAsset?.url && (
        <ImageV2
          alt={product.name || ''}
          className={styles.image}
          height={90}
          objectFit="contain"
          src={product.primaryAsset.url}
          width={70}
        />
      )}
    </Link>
  )
}

const Title = ({
  type,
  variant,
}: {
  type: string
  variant?: string
}) => {
  const { t } = useTranslation()
  const extra = variant ? ` - ${variant}` : ''

  switch (type) {
    case 'color':
      return t('products:WB_CHOOSE_COLOUR') + extra
    case 'size':
      return t('products:SIZES') + extra
    case 'variation':
      return t('products:VARIATIONS') + extra
    default:
      return type + extra
  }
}

export const LinkedProducts = ({
  products,
  type,
}: {
  products: ProductCrossSellAndPersonalizationContentFragment[] | null | undefined
  type: string
}) => {
  const [variant, setVariant] = React.useState<string | undefined>()

  if (!products || products.length === 0) {
    return null
  }

  const defaultVariant = products[0].name || undefined
  const desktopItemLimitReached = products.length > 4
  const desktopItemsVisible = desktopItemLimitReached ? 3 : 4

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>
        <Title type={type} variant={variant} />
      </div>

      <div className={clsx(styles.list, desktopItemLimitReached ? styles.full : '')}>
        {map(products, (product, index) => (
          <Item
            key={product.id}
            className={clsx(index >= desktopItemsVisible && styles.hiddenDesktop)}
            onMouseOut={() => setVariant(undefined)}
            onMouseOver={() => setVariant(product.name || undefined)}
            product={product}
            type={type}
          />
        ))}

        {desktopItemLimitReached && (
          <Drawer
            onOpenChange={(open) => {
              if (open) {
                ampli.promptOpen({
                  source_id: `linked_products_${type}`,
                })
              }

              setVariant((prev) => {
                if (open && prev !== products[0].name) {
                  return defaultVariant
                }

                if (!open && prev !== undefined) {
                  return undefined
                }

                return prev
              })
            }}
            triggerRender={(
              <button className={styles.more} type="button">
                {`+${products.length - desktopItemsVisible}`}
              </button>
            )}
          >
            <DrawerContentBasic title={<Title type={type} />}>
              <div className={styles.sidepaneTitle}>
                <Title type={type} variant={variant} />
              </div>
              <div className={styles.sidepaneProducts}>
                {map(products, product => (
                  <Item
                    key={product.id}
                    onMouseOut={() => setVariant(defaultVariant)}
                    onMouseOver={() => setVariant(product.name || defaultVariant)}
                    product={product}
                    type={type}
                  />
                ))}
              </div>
            </DrawerContentBasic>
          </Drawer>
        )}
      </div>
    </div>
  )
}
